import { useState, useCallback } from 'react';
import getSingleProspect from 'api/canprospect/getSingleProspect';
import { ProspectInterface } from 'api/canprospect/prospect';

interface UseProspectDetailReturn {
  open: boolean;
  prospect: ProspectInterface | null;
  loading: boolean;
  error: string | null;
  activeTab: number;
  setActiveTab: (tab: number) => void;
  openPanel: (id: number) => void;
  closePanel: () => void;
  refetch: () => Promise<void>;
}

export function useProspectDetail(): UseProspectDetailReturn {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [prospect, setProspect] = useState<ProspectInterface | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchProspect = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSingleProspect(id);
      if (!data) {
        setError('Prospect not found.');
        return;
      }
      setProspect(data);
    } catch (err) {
      console.error('Error fetching prospect:', err);
      setError('Failed to load prospect details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const openPanel = useCallback((id: number) => {
    setSelectedId(id);
    setActiveTab(0);
    setOpen(true);
    fetchProspect(id);
  }, [fetchProspect]);

  const closePanel = useCallback(() => {
    setOpen(false);
    setSelectedId(null);
    setProspect(null);
    setError(null);
  }, []);

  const refetch = useCallback(async () => {
    if (selectedId !== null) {
      await fetchProspect(selectedId);
    }
  }, [selectedId, fetchProspect]);

  return {
    open,
    prospect,
    loading,
    error,
    activeTab,
    setActiveTab,
    openPanel,
    closePanel,
    refetch,
  };
}

export default useProspectDetail;
